import { make } from 'vuex-pathify';
import Cookies from 'js-cookie';
import {
 addSeconds, isAfter, parseISO,
} from 'date-fns';
import { post } from '../../http';

const normalizeOrigin = (origin = '') => origin.replace(/\/$/, '');

const DATA_POWER_RAPIDKEY_HOSTS = new Set([
  'cat-services.usps.com',
  'services.usps.com',
]);

const LOCAL_RAPIDKEY_PROXY_HOSTS = new Set([
  'localhost.imagitas.com',
  'localhost',
  '127.0.0.1',
  '::1',
]);

const shouldUseLocalRapidKeyProxy = () => {
  if (process.env.NODE_ENV === 'development') {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  return LOCAL_RAPIDKEY_PROXY_HOSTS.has(window.location.hostname)
    || window.location.port === '8082'
    || window.location.port === '8080';
};

const shouldProxyRapidKeySearch = (origin = '') => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (!shouldUseLocalRapidKeyProxy()) {
    return false;
  }

  try {
    const hostname = new URL(normalizeOrigin(origin)).hostname;
    return DATA_POWER_RAPIDKEY_HOSTS.has(hostname);
  } catch {
    return false;
  }
};

const getRapidKeySearchUrl = (origin = '', path) => {
  if (shouldProxyRapidKeySearch(origin)) {
    return `/api/rapidkey${path}`;
  }

  return `${normalizeOrigin(origin)}${path}`;
};

const defaultState = {
  timeBomb: null,
  token: '',
};

const mutations = {
  ...make.mutations(defaultState),

  RESET_STATE(s) {
    Object.assign(s, defaultState);
  },
};

const getters = {
  ...make.getters(defaultState),
};

const actions = ({
  MGO_API,
}) => ({
  reset({ commit }) {
    commit('RESET_STATE');
  },

  async authenticateRapidKey({ commit, rootState }, { visitId, token }) {
    {
      const { token: rkToken, expiresAt } = JSON.parse(Cookies.get('dprk') || '{}');
      if (isAfter(parseISO(expiresAt), new Date())) {
        commit('SET_TOKEN', rkToken);
        return true;
      }
    }

// rkauthdelay use to avoid rapid repetitive attempts to obtain a RapidKey token
    if (Cookies.get('rkauthdelay') || !rootState?.config?.rapidKeyFrontendEnabled) {
      return false;
    }

// rkauthmaxretries restricts the max attempts to obtain a  RapidKey token
    const rkauthmaxretries = rootState?.config?.rkAuthMaxRetries || 5;
    const retries = Cookies.get('rkauthmaxretries');
    if (retries && parseInt(retries, 10) >= rkauthmaxretries) {
      return false;
    }

     const { access_token: rkToken, expires_in: expiresIn } = await post(`${MGO_API}/mgo-api/api/v1/datapower/rapidkey/token?visitId=${visitId}`, {}, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (rkToken) {
      commit('SET_TOKEN', rkToken);
      Cookies.set('dprk', {
        token: rkToken,
        expiresAt: addSeconds(new Date(), expiresIn),
      });
      return true;
    }

     const rkauthdelay = rootState?.config?.rkAuthDelay || 5000;
     Cookies.set('rkauthdelay', rkauthdelay, {
      expires: addSeconds(new Date(), rkauthdelay / 1000),
        sameSite: 'strict',
     });

      Cookies.set('rkauthmaxretries', ((retries && parseInt(retries, 10)) || 0) + 1,
       {
        expires: addSeconds(new Date(),
        (rootState?.config?.rkCookieExpiryDuration || 24 * 60 * 60 * 1000) / 1000), // 24 hours
        sameSite: 'strict',
      });

    return false;
  },

  async addressSearch(ctx, {
    streetAddress = '',
    cityName = '',
    state = '',
    zipcode = '',
    token,
    visitId,
    origin,
  }) {
    if (!ctx.state.token) {
      if (!await ctx.dispatch('authenticateRapidKey', { token, visitId, origin })) {
        return new Promise((resolve) => {
          resolve({});
        });
      }
    }
    // Exclude state and city from address search - MGOD-10769
    if (state || cityName) {
      // window.console.debug('RapidKey address search will exclude city and state', {
      //   streetAddress, cityName, state, zipcode,
      // });
    }
    return post(getRapidKeySearchUrl(origin, '/search/address'), {
      streetAddress, cityName: '', state: '', zip: zipcode,
    }, {
      headers: {
        Authorization: `Bearer ${ctx.state.token}`,
      },
    });
  },

  async zipcodeSearch(ctx, {
    cityName = '',
    state = '',
    zip = '',
    token,
    visitId,
    origin,
  }) {
    if (!ctx.state.token) {
      if (!await ctx.dispatch('authenticateRapidKey', { token, visitId, origin })) {
        return new Promise((resolve) => {
          resolve({});
        });
      }
    }
    return post(getRapidKeySearchUrl(origin, '/search/zcs'), {
      cityName,
      state,
      zip,
    }, {
      headers: {
        Authorization: `Bearer ${ctx.state.token}`,
      },
    });
  },
});

export default ({
  MGO_API,
}) => ({
  namespaced: true,
  state: defaultState,
  mutations,
  getters,
  actions: actions({
    MGO_API,
  }),
});
